/**
 * JWT utility functions for token management
 */

/**
 * Base64Url decode function (JWT compatible)
 */
function base64UrlDecode(str: string): string {
  // Convert from base64url to base64
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  
  // Add padding if needed
  while (base64.length % 4) {
    base64 += '=';
  }
  
  try {
    // Decode base64
    return atob(base64);
  } catch (e) {
    throw new Error('Invalid token encoding');
  }
}

/**
 * Decode a JWT token and return its payload
 * @param token JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeJwt(token: string): any {
  try {
    // JWT token consists of three parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const base64Url = parts[1];
    if (!base64Url) return null;
    
    // Decode the base64url encoded payload
    const jsonPayload = base64UrlDecode(base64Url);
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token JWT token string
 * @returns True if token is expired or invalid, false otherwise
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }
  
  // Check if token has the correct format (header.payload.signature)
  if (!token.includes('.') || token.split('.').length !== 3) {
    return true;
  }
  
  try {
    const payload = decodeJwt(token);
    
    if (!payload) {
      return true;
    }
    
    if (!payload.exp) {
      return true;
    }
    
    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;
    
    return isExpired;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
}

/**
 * Get remaining time until token expiration in seconds
 * @param token JWT token string
 * @returns Seconds until expiration or 0 if expired/invalid
 */
export function getTokenRemainingTime(token: string | null): number {
  if (!token) return 0;
  
  try {
    const payload = decodeJwt(token);
    if (!payload || !payload.exp) return 0;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingTime = payload.exp - currentTime;
    
    return remainingTime > 0 ? remainingTime : 0;
  } catch (error) {
    console.error('Error getting token remaining time:', error);
    return 0;
  }
}
